import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { forwardRef, PropsWithChildren, ReactElement, Ref } from 'react';
import { CircularProgress, Divider, Slide, useTheme, Zoom, Box, Typography, IconButton } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { Close } from '@mui/icons-material';

export type ConfirmationModalProps = PropsWithChildren<{
  isOpen: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  title?: string;
  content?: string;
  confirmButtonText?: string;
  closeButtonText?: string;
  isLoading: boolean;
  isLogin?: boolean;
  onCloseLogin?: () => void;
  optionalWidth?: string;
  confirmButtonDisabled?: boolean;
  customHeaderStyle?: {
    background?: string;
    titleColor?: string;
    icon?: React.ReactNode;
    buttonColor?: string;
  };
}>;
const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement },
  ref: Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

export const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  children,
  title,
  content,
  confirmButtonText,
  closeButtonText,
  isLoading,
  isLogin,
  onCloseLogin,
  optionalWidth,
  confirmButtonDisabled,
  customHeaderStyle,
}: ConfirmationModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog
      TransitionComponent={Transition}
      open={isOpen}
      onClose={undefined}
      fullScreen={false}
      disableEscapeKeyDown={true}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: isMobile ? '100%' : (optionalWidth ?? '500px'),
          maxHeight: '90vh',
          zIndex: 99999999,
          backgroundColor: theme.palette.grey[50],
          borderRadius: isMobile ? 1 : undefined,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {customHeaderStyle ? (
        <Box
          sx={{
            background: customHeaderStyle.background || 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            padding: { xs: '12px 16px', sm: '16px 20px' },
            borderRadius: '4px 4px 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              zIndex: 0,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
            {customHeaderStyle.icon && <Box>{customHeaderStyle.icon}</Box>}
            <Typography
              variant="h6"
              sx={{
                color: customHeaderStyle.titleColor || 'white',
                fontWeight: 700,
                fontSize: '1rem !important',
                textShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }}
            >
              {title}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: customHeaderStyle.titleColor || 'white',
              position: 'relative',
              zIndex: 1,
              width: 36,
              height: 36,
              border: '1px solid',
              borderColor: 'rgba(255,255,255,0.3)',
              backgroundColor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.5)',
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: { xs: '12px 16px', sm: '16px 20px' } }}>
          <Typography variant="h6" sx={{ color: '#0b6bcb', fontWeight: 700, fontSize: '1rem !important' }}>
            {title}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: '#0b6bcb',
              width: 36,
              height: 36,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              '&:hover': {
                backgroundColor: 'action.hover',
                borderColor: 'primary.main',
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      )}
      {!customHeaderStyle && <Divider sx={{ height: '2px', color: '#0b6bcb' }} />}
      <Zoom in={true} timeout={500}>
        <DialogContent sx={{ m: 0, px: { xs: 2, sm: 3 }, py: 2, '&.MuiDialogContent-root': { m: 0 } }}>
          {content && (
            <DialogContentText sx={{ m: 0, mb: 1, color: 'black' }} id="alert-dialog-description">
              {content}
            </DialogContentText>
          )}
          {children}
        </DialogContent>
      </Zoom>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            color: confirmButtonText === 'Sil' ? `#dc2626 !important` : '#0b6bcb !important',
            border: confirmButtonText === 'Sil' ? `1px solid #dc2626` : '1px solid #0b6bcb',
            display: closeButtonText === '' ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.875rem',
            height: '30px',
            padding: '4px 16px',
          }}
        >
          {closeButtonText || 'Vazgeç'}
        </Button>
        <Button
          disabled={isLoading || confirmButtonDisabled}
          sx={{
            color: 'white !important',
            height: '30px',
            minWidth: '120px',
            maxWidth: '300px',
            padding: '4px 16px !important',
            fontSize: '0.875rem',
            display: confirmButtonText === '' ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
            background:
              (confirmButtonText === 'Sil'
                ? 'linear-gradient(135deg,#dc2626 0%, #b91c1c 100%)'
                : customHeaderStyle?.buttonColor) || '#0b6bcb',
            '&:hover': {
              background: customHeaderStyle?.buttonColor
                ? `linear-gradient(0deg, rgba(0,0,0,0.2), rgba(0,0,0,0.2)), ${customHeaderStyle.buttonColor}`
                : '#084a8c',
              color: 'white',
            },
            '&.Mui-disabled': {
              background: customHeaderStyle?.buttonColor ? `${customHeaderStyle.buttonColor}` : '#0b6bcb80',
              color: 'white !important',
            },
          }}
          onClick={onConfirm}
          autoFocus
        >
          {!isLoading ? (
            confirmButtonText || 'Onayla'
          ) : (
            <CircularProgress sx={{ width: '20px !important', height: '20px !important', color: 'white' }} />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
